import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PhoneButtonTypes } from '../../models/phone-button';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import {
  Button,
  DirectoryNumberRef,
  Global360View,
  LineFeatureFieldConfig,
  Phone,
} from '../../models/generated/ziroModels';
import { PhoneButtonsService } from '../shared/phone-buttons.service';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { DragDropMode } from '../drag-drop-mode.enum';
import { ZiroFormConfig, ZiroFormsUpdate } from '../../../forms/ziro-forms-models';
import { LineButtonPeripheralSummary } from '../layout/phone-buttons-standard.component';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { PhoneButtonsLineHelpdeskComponent } from './helpdesk/phone-buttons-line-helpdesk.component';
import { ExtensionMobilityDnD } from '../phone-buttons.component';
import { Observable } from 'rxjs';
import { PhoneButtonsSpeedDialComponent } from './phone-buttons-shared/phone-buttons-speed-dial.component';
import { PhoneButtonsBlfComponent } from './phone-buttons-shared/phone-buttons-blf';
import { PhoneButtonsServiceUrlHelpdeskComponent } from './helpdesk/phone-buttons-service-url-helpdesk.component';
import { PhoneButtonsIntercomComponent } from './phone-buttons-shared/phone-buttons-intercom.component';
import { PhoneButtonsLineComponent } from './phone-buttons-shared/phone-buttons-line.component';
import { ZiroFormsModule } from '../../../forms/ziro-forms.module';

interface ButtonTypeFormData {
  type: ZiroSelectOption;
}

@Component({
  selector: 'ziro-phone-buttons-edit-form',
  templateUrl: './phone-buttons-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ZiroFormsModule,
    PhoneButtonsSpeedDialComponent,
    PhoneButtonsBlfComponent,
    PhoneButtonsServiceUrlHelpdeskComponent,
    PhoneButtonsIntercomComponent,
    PhoneButtonsLineHelpdeskComponent,
    PhoneButtonsLineComponent,
  ],
})
export class PhoneButtonsEditFormComponent
  extends ZiroFormAbstractDirective<Button, ButtonTypeFormData>
  implements OnInit, AfterViewInit, OnChanges
{
  protected _ziroFormStateService: ZiroFormStateService;
  phoneButtonsService = inject(PhoneButtonsService);

  @ViewChild('form') childForm: ZiroFormAbstractDirective<Button>;
  @ViewChild(PhoneButtonsLineHelpdeskComponent) lineHelpdeskForm: PhoneButtonsLineHelpdeskComponent;

  readonly userMode = input<DragDropMode>();

  // the below inputs are only relevant in DragDropMode.HELPDESK
  readonly isFixedButton = input<boolean>();
  readonly siteId = input<number>();
  readonly lineButtonSummary = input<LineButtonPeripheralSummary>();
  readonly lineFieldConfig = input<LineFeatureFieldConfig>();
  readonly phone = input<Phone | ExtensionMobilityDnD>();
  readonly buttonIndex = input<number>();
  readonly isAutomaticPhoneTemplateSelectionEnabled = input<boolean>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() buttonTypeOptions: ZiroSelectOption[];
  // only sent in helpdesk mode
  readonly global360View = input<Global360View>();
  readonly validateChildFormFields = input<boolean>();
  readonly linePeripheralSummaryUpdated = output<LineButtonPeripheralSummary>();
  readonly lineButtonDnUpdated = output<any>();

  ButtonTypes = PhoneButtonTypes;
  DragDropMode = DragDropMode;

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    if (!this.buttonTypeOptions?.length) {
      this.buttonTypeOptions = [{ value: this.entity.type, label: this.entity.type }];
    } else if (!this.buttonTypeOptions.find((option) => option.value === this.entity.type)) {
      // Add the existing button type to buttonTypeOptions when not present in metadata cache.
      this.buttonTypeOptions.push({ value: this.entity.type, label: this.entity.type });
    }

    this.formConfig = {
      fields: {
        type: {
          label: 'tkey;pages.details.phoneSettings.button.type',
          dataAutomation: 'phone-button-types',
          componentConfig: new ZiroSelectConfig({ options: this.buttonTypeOptions.filter((opt) => !!opt.group) }),
          required: true,
          hidden: () => this.userMode() === DragDropMode.SELF_SERVE,
          disabled: () => this.isFixedButton() || !this.isAutomaticPhoneTemplateSelectionEnabled(),
          disabledTooltip: this.isFixedButton()
            ? 'tkey;shared.device.phone.phone_buttons.type.fixed.disabled_tooltip'
            : 'tkey;shared.device.phone.phone_buttons.type.disabled_tooltip',
        },
      },
      options: {
        columnClasses: {
          label: 'col-3 text-lg-end',
          input: 'col-9',
        },
      },
    };
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // todo this might be whats causing the race condition
    setTimeout(() => {
      this.fieldChannels.type.ziroFormUpdate$.subscribe((update: ZiroFormsUpdate<ZiroSelectOption>) => {
        if (update.new?.value && update.old?.value && update.new.value !== update.old.value) {
          const newButton = this.phoneButtonsService.newButtonOfType(update.new.value);
          this.entitySource.next(newButton);
        }
      });
    }, 0);

    // When this form is submitted, trigger a submit on the child.
    this._validateAndSubmitSource.subscribe((submit) => this.childForm._validateAndSubmitSource.next(submit));
  }

  handleButtonChange<T extends Button>(update: ZiroFormsUpdate<T>): void {
    this.validationState = update.valid;
    this.formData = this.toFormData(update.new);
    const oldEntity = this.entity;
    this.entity = this.toEntity(this.formData);

    this.ziroFormsUpdateSource.next({
      old: oldEntity,
      new: this.entity,
      valid: this.validationState,
    });
  }

  handleVoicemailChange(update: LineButtonPeripheralSummary): void {
    this.linePeripheralSummaryUpdated.emit(update);
  }

  handleDnChange($event: any, dnRef: DirectoryNumberRef): void {
    this.lineButtonDnUpdated.emit({ update: $event, dnRef: dnRef });
  }

  submit(): Observable<any> {
    return this.parentSubmit$();
  }

  toFormData = (entity: Button): ButtonTypeFormData => {
    return {
      ...entity,
      type: this.buttonTypeOptions.find((option) => option.value === entity.type) ?? {
        value: this.entity.type,
        label: this.entity.type,
      },
    };
  };

  toEntity = (formData: ButtonTypeFormData): Button => {
    return {
      ...formData,
      type: formData.type.value,
    };
  };

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (
      !!changes.validateChildFormFields &&
      !changes.validateChildFormFields.isFirstChange() &&
      changes.validateChildFormFields.previousValue &&
      this.lineHelpdeskForm.lineHelpdeskFormComponent?.fieldChannels
    ) {
      Object.keys(this.lineHelpdeskForm.lineHelpdeskFormComponent?.fieldChannels).forEach((key) => {
        this.lineHelpdeskForm.lineHelpdeskFormComponent.fieldChannels[key].validateSource.next();
      });
    }
  }
}
